import { Component, inject, OnInit } from "@angular/core";
import { Meta } from "@angular/platform-browser";
import { ActivatedRoute } from "@angular/router";
import { CategoriesModel } from "src/models/categories.model";
import { Requests } from "src/models/requests";
import { CategoriesService } from "src/services/categories/categories.service";
import { RequestsService } from "src/services/requests/requests.service";
import {load} from "@angular-devkit/build-angular/src/utils/server-rendering/esm-in-memory-file-loader";
import { retryWhen, delay, take, tap, mergeMap } from "rxjs/operators";
import { of, throwError } from "rxjs";

@Component({
    selector: "app-requests",
    templateUrl: "./requests.component.html",
    styleUrls: ["./requests.component.scss"],
})
export class RequestsComponent implements OnInit {
    requests: Array<Requests>;
    categories: Array<CategoriesModel>;
    loading: boolean;
    slug: string;
    search: string;
    filtRequests: Array<Requests>;
    page = 1;
    pageSize = 50;
    collectionSize = 0;
    
    // Nouvelles propriétés pour les filtres avancés
    showAdvancedFilters = false;
    statusFilter = '';
    urgencyFilter = '';
    dateFilter = '';
    viewMode = 'grid';
    selectedCategory = '';
    
    // Propriétés calculées pour les statistiques
    get activeRequests() {
        return this.requests?.filter(req => req.status === 'published') || [];
    }
    
    get urgentRequests() {
        return this.requests?.filter(req => req.jour_restant <= 7) || [];
    }
    
    get completedRequests() {
        return this.requests?.filter(req => ['expired', 'rejected', 'aborded'].includes(req.status)) || [];
    }

    constructor(
        private requestService: RequestsService,
        public categoryService: CategoriesService,
        private route: ActivatedRoute,
        private meta: Meta
    ) {}

    ngOnInit(): void {
        this.meta.addTag({ name: 'description', content: "Appels d'offres" });
        this.meta.addTag({ name: 'keywords', content: 'BSTP Guinée, BSTP, bstpgn, ' });
        this.meta.addTag({ name: 'author', content: 'BSTP Guinée' });

        this.slug = this.route.snapshot.params["slug"];
        console.log(this.slug)
        this.loading = true;
        if (this.slug != null) {
            this.requestService.filter(this.slug).pipe(
                retryWhen(errors =>
                  errors.pipe(
                    mergeMap((error) => {
                      // Si c'est une erreur serveur (500+), on réessaie après un délai
                      if (error.status >= 500) {
                        console.log("Erreur serveur, nouvelle tentative...");
                        return of(error).pipe(delay(500));
                      }
                      // Sinon (404, 400, etc.), on propage immédiatement l'erreur
                      return throwError(() => error);
                    }),
                    take(3) // Max 3 tentatives pour les erreurs 5xx
                  )
                )
              ).subscribe({
                next: (data) => {
                    this.loading = false;
                    this.requests = data;
                    this.collectionSize = this.requests.length;
                    this.filtRequests = this.requests.slice(0, this.pageSize);
                    this.animateCounters();
                },
                error: (error) => {
                    this.loading = false;
                },
            });
        } else {
            this.requestService.all().pipe(
              retryWhen(errors =>
                errors.pipe(
                  mergeMap((error) => {
                    // Si c'est une erreur serveur (500+), on réessaie après un délai
                    if (error.status >= 500) {
                      console.log("Erreur serveur, nouvelle tentative...");
                      return of(error).pipe(delay(500));
                    }
                    // Sinon (404, 400, etc.), on propage immédiatement l'erreur
                    return throwError(() => error);
                  }),
                  take(3) // Max 3 tentatives pour les erreurs 5xx
                )
              )
            ).subscribe({
                next: (data) => {
                    this.loading = false;
                    this.requests = data;
                    this.collectionSize = this.requests.length;
                    this.filtRequests = this.requests.slice(0, this.pageSize);
                    this.animateCounters();
                },
                error: (error) => {
                    this.loading = false;
                },
            });
        }
    }

    onClick(slug) {
        this.selectedCategory = slug;
        this.loading = true;
        this.requestService.filter(slug).pipe(
            retryWhen(errors =>
              errors.pipe(
                mergeMap((error) => {
                  // Si c'est une erreur serveur (500+), on réessaie après un délai
                  if (error.status >= 500) {
                    console.log("Erreur serveur, nouvelle tentative...");
                    return of(error).pipe(delay(500));
                  }
                  // Sinon (404, 400, etc.), on propage immédiatement l'erreur
                  return throwError(() => error);
                }),
                take(3) // Max 3 tentatives pour les erreurs 5xx
              )
            )
          ).subscribe({
            next: (data) => {
                this.loading = false;
                this.requests = data;
                this.collectionSize = this.requests.length;
                this.filtRequests = this.requests.slice(0, this.pageSize);
            },
            error: (error) => {
                this.loading = false;
            },
        });
    }

    get filteredRequests() {
        if (this.search === null || this.search === undefined || this.search === '') {
            return this.filtRequests;
        }

        return this.filtRequests.filter(request =>
            request.name.toLowerCase().includes(this.search.toLowerCase())
        );
    }

    refreshCompanies() {
        // Map the formations to add an `id` property to each one.
        const mappedCompanies = this.requests.map((formation, i) => ({ id: i + 1, ...formation }));

        // Calculate the starting and ending indices for pagination.
        const startIndex = (this.page - 1) * this.pageSize;
        const endIndex = startIndex + this.pageSize;

        // Slice the mapped formations array to get only the formations for the current page.
        this.filtRequests = mappedCompanies.slice(startIndex, endIndex);
    }
    
    // Nouvelles méthodes pour l'interactivité
    toggleAdvancedFilters() {
        this.showAdvancedFilters = !this.showAdvancedFilters;
    }
    
    onSearchInput() {
        this.applyFilters();
    }
    
    applyFilters() {
        let filtered = this.requests || [];
        
        // Filtre par recherche
        if (this.search) {
            filtered = filtered.filter(req => 
                req.name.toLowerCase().includes(this.search.toLowerCase()) ||
                (req.description && req.description.toLowerCase().includes(this.search.toLowerCase()))
            );
        }
        
        // Filtre par statut
        if (this.statusFilter) {
            filtered = filtered.filter(req => req.status === this.statusFilter);
        }
        
        // Filtre par urgence
        if (this.urgencyFilter) {
            if (this.urgencyFilter === 'urgent') {
                filtered = filtered.filter(req => req.jour_restant <= 7);
            } else if (this.urgencyFilter === 'normal') {
                filtered = filtered.filter(req => req.jour_restant > 7);
            }
        }
        
        // Filtre par date
        if (this.dateFilter) {
            const today = new Date();
            const startOfDay = new Date(today.getFullYear(), today.getMonth(), today.getDate());
            
            filtered = filtered.filter(req => {
                const publishDate = new Date(req.published_at);
                
                switch (this.dateFilter) {
                    case 'today':
                        return publishDate >= startOfDay;
                    case 'week':
                        const weekAgo = new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000);
                        return publishDate >= weekAgo;
                    case 'month':
                        const monthAgo = new Date(today.getFullYear(), today.getMonth() - 1, today.getDate());
                        return publishDate >= monthAgo;
                    default:
                        return true;
                }
            });
        }
        
        this.filtRequests = filtered;
        this.collectionSize = filtered.length;
    }
    
    clearFilters() {
        this.search = '';
        this.statusFilter = '';
        this.urgencyFilter = '';
        this.dateFilter = '';
        this.showAdvancedFilters = false;
        this.selectedCategory = '';
        this.filtRequests = this.requests;
        this.collectionSize = this.requests?.length || 0;
    }
    
    setViewMode(mode: 'grid' | 'list') {
        this.viewMode = mode;
    }
    
    getStatusClass(status: string): string {
        switch (status) {
            case 'published': return 'status-active';
            case 'expired': return 'status-expired';
            case 'rejected': return 'status-rejected';
            case 'aborded': return 'status-cancelled';
            default: return 'status-draft';
        }
    }
    
    getStatusIcon(status: string): string {
        switch (status) {
            case 'published': return 'flaticon-check';
            case 'expired': return 'flaticon-clock';
            case 'rejected': return 'flaticon-close';
            case 'aborded': return 'flaticon-cancel';
            default: return 'flaticon-draft';
        }
    }
    
    getStatusText(status: string): string {
        switch (status) {
            case 'published': return 'EN COURS';
            case 'expired': return 'EXPIRÉ';
            case 'rejected': return 'REJETÉ';
            case 'aborded': return 'ANNULÉ';
            default: return 'BROUILLON';
        }
    }
    
    viewDetails(request: Requests) {
        // Navigation vers la page de détails
        console.log('Voir détails:', request);
    }
    
    animateCounters() {
        setTimeout(() => {
            const counters = document.querySelectorAll('.stat-number');
            counters.forEach(counter => {
                const target = parseInt(counter.getAttribute('data-count') || '0');
                const duration = 2000;
                const increment = target / (duration / 16);
                let current = 0;
                
                const timer = setInterval(() => {
                    current += increment;
                    if (current >= target) {
                        current = target;
                        clearInterval(timer);
                    }
                    counter.textContent = Math.floor(current).toString();
                }, 16);
            });
        }, 500);
    }
}
